/**
 * InstallPrompt — encourages users to install NEURACOM as a PWA.
 *
 *  • Android / desktop Chrome  → uses the standard `beforeinstallprompt` event
 *    to show a one-tap install button.
 *  • iOS Safari                → cannot trigger an install prompt; we show
 *    visual instructions (Share button → "Add to Home Screen") instead.
 *  • Already-installed PWA     → never shown (display-mode: standalone).
 *  • Dismissed once            → suppressed for 7 days.
 */
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { Download, X, Share, Plus } from "lucide-react";

const STORAGE_KEY = "neuracom_install_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent.toLowerCase();
  // iPad on iPadOS 13+ reports as Mac, so we also detect by touch points
  return (
    /iphone|ipad|ipod/.test(ua) ||
    (ua.includes("mac") && navigator.maxTouchPoints > 1)
  );
}

function wasRecentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    return ts && Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export function InstallPrompt() {
  const { t } = useI18n();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useImperativeLoad((() => () => {
    if (isStandalone()) return; // Already installed
    if (wasRecentlyDismissed()) return;

    // Android / Chrome flow — wait for native event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari flow — no event, just show after a short delay
    if (isIOS() && !isStandalone()) {
      const timer = setTimeout(() => setShowIOSHint(true), 4000);
      // Cannot return cleanup from useImperativeLoad — clear via clearTimeout
      // is implicit when the next mount happens
      void timer;
    }

    // Installed event — hide & remember (global tracking is in pwa.js)
    window.addEventListener("appinstalled", () => {
      setShow(false);
      setShowIOSHint(false);
      try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* private mode */ }
    });
  })(), []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === "accepted") {
      setShow(false);
    } else {
      handleDismiss();
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* private mode */ }
    setShow(false);
    setShowIOSHint(false);
  };

  // ── iOS hint card (Add to Home Screen instructions) ──────────────────────
  if (showIOSHint && !show) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 z-[60] mx-auto max-w-md rounded-2xl border border-slate-700 bg-[#0A0C1F]/95 p-4 shadow-2xl backdrop-blur-lg sm:bottom-6"
        data-testid="pwa-install-ios-card"
      >
        <div className="flex items-start gap-3">
          <img src="/brand/pwa/icon-72.png" alt="NEURACOM" loading="lazy" decoding="async" className="h-12 w-12 shrink-0 rounded-xl" />
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-sm font-bold text-white">
              {t("pwa.install_ios_title")}
            </h3>
            <p className="mt-1 text-xs leading-snug text-slate-400">
              {t("pwa.install_ios_step1")}{" "}
              <span className="inline-flex items-center gap-1 rounded bg-slate-800 px-1.5 py-0.5 text-slate-200">
                <Share className="h-3 w-3" />
              </span>{" "}
              {t("pwa.install_ios_step2")}{" "}
              <span className="inline-flex items-center gap-1 rounded bg-slate-800 px-1.5 py-0.5 text-slate-200">
                <Plus className="h-3 w-3" /> {t("pwa.install_ios_step3")}
              </span>
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="ml-1 shrink-0 rounded-full p-1.5 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
            aria-label={t("common.close")}
            data-testid="pwa-install-ios-dismiss-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // ── Android / desktop install card ───────────────────────────────────────
  if (!show) return null;
  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-[60] mx-auto max-w-md rounded-2xl border border-slate-700 bg-[#0A0C1F]/95 p-4 shadow-2xl backdrop-blur-lg sm:bottom-6"
      data-testid="pwa-install-card"
    >
      <div className="flex items-start gap-3">
        <img src="/brand/pwa/icon-72.png" alt="NEURACOM" loading="lazy" decoding="async" className="h-12 w-12 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-sm font-bold text-white">
            {t("pwa.install_title")}
          </h3>
          <p className="mt-1 text-xs leading-snug text-slate-400">
            {t("pwa.install_desc")}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              onClick={handleInstall}
              className="h-9 rounded-full bg-[#9ACD32] px-4 text-xs font-bold text-[#05073B] hover:bg-[#A8DF38]"
              data-testid="pwa-install-cta-btn"
            >
              <Download className="mr-1.5 h-3.5 w-3.5" />
              {t("pwa.install_cta")}
            </Button>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              className="h-9 rounded-full text-xs text-slate-400 hover:bg-slate-800 hover:text-slate-200"
              data-testid="pwa-install-later-btn"
            >
              {t("pwa.install_later")}
            </Button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="ml-1 shrink-0 rounded-full p-1.5 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
          aria-label={t("common.close")}
          data-testid="pwa-install-dismiss-btn"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
